import UI from 'lib/ui';
import E3TranslateService from 'lib/service/e3.viewer.translate.service';

const FILL_DURATION_MS = 5000;

/**
 * Between-scene popup for museum guided tours.
 *
 * Shows a centered dialog after all guide points in a scene finish.
 * - "Look around" / clicking the backdrop — shrinks to a mini "Next →" button
 *   in the bottom-right corner (click-only, no auto-advance).
 * - "Continue touring →" — Netflix-style fill sweeps left-to-right; advances
 *   automatically when full, or immediately on click.
 * - call popup.destroy() to remove without advancing (e.g. on tour stop).
 */
export default class MuseumSceneEndPopup {
  constructor({ onNext, onLookAround }) {
    this._onNext = onNext;
    this._onLookAround = onLookAround || null;
    this._resolved = false;
    this._minimized = false;
    this._fullEl = null;
    this._miniEl = null;
    const ts = new E3TranslateService();
    this._t = (key, fallback) => {
      const val = ts.translate(key);
      return (val && val !== key) ? val : fallback;
    };
    this._build();
  }

  _build() {
    const { overlay, actions } = UI.modal({
      onBackdropClick: () => this._minimize(),
      message: this._t('MUSEUM_POPUP_MSG', 'Do you have any questions, or would you like to look around a little longer?'),
    });

    actions.appendChild(UI.btn(this._t('MUSEUM_LOOK_AROUND', 'Look around'), () => this._minimize(), 'museum-scene-end__btn-look'));

    const nextBtn = UI.btn(null, () => this._resolve(), 'museum-scene-end__btn-next');
    nextBtn.appendChild(UI.animatedFill(FILL_DURATION_MS, () => this._resolve()));
    nextBtn.appendChild(Object.assign(document.createElement('span'), { textContent: this._t('MUSEUM_CONTINUE_TOURING', 'Continue touring →') }));
    actions.appendChild(nextBtn);

    this._fullEl = overlay;
  }

  _minimize() {
    if (!this._fullEl) return;
    this._minimized = true;

    if (this._onLookAround) this._onLookAround();

    // Animate the popup container out
    const container = this._fullEl.querySelector('.museum-scene-end__container');
    if (container) {
      container.style.animation = 'museumPopupOut 0.28s cubic-bezier(0.4, 0, 1, 1) forwards';
    }
    const backdrop = this._fullEl.querySelector('.museum-scene-end__backdrop');
    if (backdrop) {
      backdrop.style.transition = 'opacity 0.28s ease';
      backdrop.style.opacity = '0';
      backdrop.style.pointerEvents = 'none';
    }

    setTimeout(() => {
      if (this._fullEl && this._fullEl.parentNode) {
        this._fullEl.parentNode.removeChild(this._fullEl);
        this._fullEl = null;
      }
    }, 280);

    this._miniEl = UI.floatingBtn(this._t('MUSEUM_NEXT', 'Next →'), () => this._resolve());
  }

  _resolve() {
    if (this._resolved) return;
    this._resolved = true;
    this.destroy();
    if (this._onNext) this._onNext(this._minimized);
  }

  destroy() {
    if (this._fullEl && this._fullEl.parentNode) {
      this._fullEl.parentNode.removeChild(this._fullEl);
      this._fullEl = null;
    }
    if (this._miniEl) { this._miniEl.remove(); this._miniEl = null; }
  }
}
